// Three-way pill toggle for the Pulse dimension: Country / Actor / Family.
// Mirrors the look of TimeRangeControl but with a wider "pill" aesthetic
// from the v4 mockup. Reads/writes the `?dimension=` URL param so reloads
// preserve state and the value is sharable.
import { useSearchParams } from 'react-router-dom';

export type Dimension = 'country' | 'actor' | 'family';

const OPTIONS: { value: Dimension; label: string }[] = [
  { value: 'country', label: 'Country' },
  { value: 'actor', label: 'Actor' },
  { value: 'family', label: 'Family' },
];

export function useDimension(defaultDim: Dimension = 'country'): Dimension {
  const [params] = useSearchParams();
  const raw = (params.get('dimension') || defaultDim) as Dimension;
  return OPTIONS.some((o) => o.value === raw) ? raw : defaultDim;
}

export default function DimensionToggle({
  defaultDim = 'country',
}: { defaultDim?: Dimension } = {}) {
  const [params, setParams] = useSearchParams();
  const current = useDimension(defaultDim);

  const setDim = (value: Dimension) => {
    const next = new URLSearchParams(params);
    next.set('dimension', value);
    setParams(next, { replace: true });
  };

  return (
    <div className="pulse-dim-toggle" role="tablist" aria-label="Pulse dimension">
      {OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="tab"
          aria-selected={current === opt.value}
          className={`pulse-dim-btn${current === opt.value ? ' active' : ''}`}
          onClick={() => setDim(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
